import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { Upload, File, Download, Eye, Plus, X, Lock } from 'lucide-react';
import EvidenceUpload from './EvidenceUpload';

const EvidenceTab = ({ caseId, assignedTo, user }) => {
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [viewingFileUrl, setViewingFileUrl] = useState(null);
  const [viewingFileType, setViewingFileType] = useState(null);

  // Password prompt for protected evidence.
  const [passwordPrompt, setPasswordPrompt] = useState(null); // { action, item }
  const [passwordValue, setPasswordValue] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchEvidence();
  }, [caseId]);

  const fetchEvidence = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/evidence/case/${caseId}`);
      setEvidence(response.data.data || []);
    } catch (error) {
      console.error('Error fetching evidence:', error);
      toast.error('Failed to load evidence');
    } finally {
      setLoading(false);
    }
  };

  const canUpload = user?.role === 'Administrator' || 
                   user?.role === 'Supervisor' || 
                   user?.role === 'Client' ||
                   (user?.role === 'Investigator' && (assignedTo?.id === user?.id || assignedTo?._id === user?._id));

  // Extract a JSON error (message + passwordRequired) from an axios error whose
  // body may be a Blob (because responseType was 'blob').
  const parseBlobError = async (error) => {
    const resp = error.response;
    let message = error.message;
    let passwordRequired = false;
    if (resp?.data instanceof Blob) {
      const ct = resp.headers?.['content-type'] || '';
      if (ct.includes('application/json')) {
        try {
          const data = JSON.parse(await resp.data.text());
          message = data.message || message;
          passwordRequired = Boolean(data.passwordRequired);
        } catch (e) {
          /* ignore */
        }
      }
    } else if (resp?.data && typeof resp.data === 'object') {
      message = resp.data.message || message;
      passwordRequired = Boolean(resp.data.passwordRequired);
    }
    return { message, passwordRequired, status: resp?.status };
  };

  // Fetch + preview the file. Returns { ok } or { ok:false, authFail, message }.
  const doView = async (evidenceItem, password) => {
    try {
      const config = { responseType: 'blob' };
      if (password) config.params = { password };
      const response = await api.get(`/evidence/${evidenceItem.id || evidenceItem._id}/view`, config);

      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        const errorData = JSON.parse(await response.data.text());
        throw new Error(errorData.message || 'Failed to load file');
      }
      const blobUrl = URL.createObjectURL(response.data);
      setViewingFileUrl(blobUrl);
      setViewingFileType(evidenceItem.fileType?.toLowerCase());
      return { ok: true };
    } catch (error) {
      const info = await parseBlobError(error);
      if (info.status === 401 || info.passwordRequired) {
        return { ok: false, authFail: true, message: info.message };
      }
      toast.error(info.message || 'Failed to view evidence.');
      return { ok: false };
    }
  };

  // Download the file. Returns { ok } or { ok:false, authFail, message }.
  const doDownload = async (evidenceItem, password) => {
    try {
      const config = { responseType: 'blob' };
      if (password) config.params = { password };
      const response = await api.get(`/evidence/${evidenceItem.id || evidenceItem._id}/download`, config);

      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        const errorData = JSON.parse(await response.data.text());
        throw new Error(errorData.message || 'Failed to download file');
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', evidenceItem.originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Evidence downloaded successfully');
      return { ok: true };
    } catch (error) {
      const info = await parseBlobError(error);
      if (info.status === 401 || info.passwordRequired) {
        return { ok: false, authFail: true, message: info.message };
      }
      toast.error(info.message || 'Failed to download evidence.');
      return { ok: false };
    }
  };

  // Button handlers: prompt for a password only when the item is protected.
  const handleView = (evidenceItem) => {
    if (evidenceItem.passwordProtected) {
      setPasswordValue('');
      setPasswordError('');
      setPasswordPrompt({ action: 'view', item: evidenceItem });
    } else {
      doView(evidenceItem, null);
    }
  };

  const handleDownload = (evidenceItem) => {
    if (evidenceItem.passwordProtected) {
      setPasswordValue('');
      setPasswordError('');
      setPasswordPrompt({ action: 'download', item: evidenceItem });
    } else {
      doDownload(evidenceItem, null);
    }
  };

  const submitPassword = async (e) => {
    if (e) e.preventDefault();
    if (!passwordPrompt || submitting) return;
    if (!passwordValue.trim()) {
      setPasswordError('Please enter the password');
      return;
    }
    setSubmitting(true);
    setPasswordError('');
    const { action, item } = passwordPrompt;
    const result = action === 'view'
      ? await doView(item, passwordValue)
      : await doDownload(item, passwordValue);
    setSubmitting(false);
    if (result.ok) {
      setPasswordPrompt(null);
      setPasswordValue('');
    } else if (result.authFail) {
      setPasswordError(result.message || 'Incorrect password');
    } else {
      setPasswordPrompt(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Evidence</h3>
        {canUpload && (
          <button
            onClick={() => setShowUpload(!showUpload)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {showUpload ? (
              <>
                <File className="w-4 h-4" />
                <span>Cancel</span>
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                <span>Upload Evidence</span>
              </>
            )}
          </button>
        )}
      </div>

      {showUpload && canUpload && (
        <div className="bg-gray-50 rounded-lg p-6">
          <EvidenceUpload
            caseId={caseId}
            onSuccess={() => {
              setShowUpload(false);
              fetchEvidence();
            }}
          />
        </div>
      )}

      {evidence.length === 0 ? (
        <div className="text-center py-12">
          <File className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No evidence uploaded yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {evidence.map((item) => (
            <div
              key={item._id}
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <File className="w-5 h-5 text-blue-600" />
                    <h4 className="font-medium text-gray-900">{item.originalName}</h4>
                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                      {item.fileType}
                    </span>
                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                      {(item.fileSize / 1024).toFixed(2)} KB
                    </span>
                    {item.passwordProtected && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded"
                        title="Password protected"
                      >
                        <Lock className="w-3 h-3" /> Protected
                      </span>
                    )}
                  </div>
                  {item.description && (
                    <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                  )}
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span>Uploaded by: {item.uploadedBy?.firstName} {item.uploadedBy?.lastName}</span>
                    <span>Uploaded: {new Date(item.createdAt).toLocaleDateString()}</span>
                    {item.scanResult && (
                      <span className={`px-2 py-1 rounded ${
                        item.scanResult === 'Clean' ? 'bg-green-100 text-green-800' :
                        item.scanResult === 'Threat Detected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        Scan: {item.scanResult}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleView(item)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                    title="View"
                  >
                    <Eye className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDownload(item)}
                    className="p-2 text-green-600 hover:bg-green-50 rounded"
                    title="Download"
                  >
                    <Download className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Password prompt for protected evidence */}
      {passwordPrompt && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => { if (!submitting) { setPasswordPrompt(null); setPasswordValue(''); } }}
        >
          <form
            onSubmit={submitPassword}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg shadow-xl w-full max-w-md"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-amber-600" />
                <h3 className="text-base font-semibold text-gray-900">
                  {passwordPrompt.action === 'download' ? 'Download protected evidence' : 'View protected evidence'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => { if (!submitting) { setPasswordPrompt(null); setPasswordValue(''); } }}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4">
              <p className="text-sm text-gray-600 mb-3 truncate">{passwordPrompt.item.originalName}</p>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                autoFocus
                value={passwordValue}
                onChange={(e) => { setPasswordValue(e.target.value); setPasswordError(''); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter evidence password"
              />
              {passwordError && <p className="mt-2 text-sm text-red-600">{passwordError}</p>}
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => { if (!submitting) { setPasswordPrompt(null); setPasswordValue(''); } }}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? 'Verifying…' : (passwordPrompt.action === 'download' ? 'Download' : 'View')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* File Viewer Popup */}
      {viewingFileUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Viewing Evidence</h3>
              <button
                onClick={() => {
                  if (viewingFileUrl) {
                    URL.revokeObjectURL(viewingFileUrl);
                  }
                  setViewingFileUrl(null);
                  setViewingFileType(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {viewingFileType && (viewingFileType === 'jpeg' || viewingFileType === 'jpg' || viewingFileType === 'png' || viewingFileType === 'gif' || viewingFileType === 'webp') ? (
                <div className="flex items-center justify-center">
                  <img 
                    src={viewingFileUrl} 
                    alt="Evidence" 
                    className="max-w-full max-h-[70vh] object-contain"
                  />
                </div>
              ) : viewingFileType === 'pdf' ? (
                <div className="w-full h-[70vh]">
                  <iframe 
                    src={viewingFileUrl} 
                    className="w-full h-full border-0"
                    title="PDF Viewer"
                  />
                </div>
              ) : viewingFileType && (viewingFileType === 'mp4' || viewingFileType === 'avi' || viewingFileType === 'mov' || viewingFileType === 'wmv' || viewingFileType === 'mpeg' || viewingFileType === 'webm') ? (
                <div className="flex items-center justify-center">
                  <video 
                    src={viewingFileUrl} 
                    controls 
                    className="max-w-full max-h-[70vh]"
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              ) : viewingFileType === 'csv' ? (
                <div className="w-full h-[70vh] overflow-auto">
                  <iframe 
                    src={viewingFileUrl} 
                    className="w-full h-full border-0"
                    title="CSV Viewer"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[70vh]">
                  <File className="w-16 h-16 text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-4">This file type cannot be previewed.</p>
                  <a
                    href={viewingFileUrl}
                    download
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Download File
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvidenceTab;

